import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import _pick from 'lodash/pick';

import Button from '@components/_shared/button';
import { AmenitiesSelection } from '@components/search/amenitiesSelection';
import { MinMaxInputs } from '@components/search/minMaxInputs';
import { MinMaxSelectors } from '@components/search/minMaxSelectors';
import Select from '@components/_shared/select';
import {
  bedroomsOptions,
  guestsOptions,
  renderBedroomsValue,
  renderPriceValue,
} from '@components/search/utils';
import { useClickOutside } from '@utils/hooks/useClickOutside';
import { BaseComponentProps } from '@utils/types/baseComponents';
import { FilterParamsType } from '@utils/types/searchListingsContext';
import { MarketType } from '@utils/types/city';
import { NeighborhoodType } from '@utils/types/neighborhoods';
import { LocationSelectors } from '@components/search/locationSelectors';

import styles from './styles.module.scss';
import { CloseButton } from '@components/_shared/closeButton';

type Props = {
  activeFilters: FilterParamsType;
  onApply: (value: FilterParamsType) => void;
  markets: MarketType[];
  neighborhoods: NeighborhoodType[];
} & BaseComponentProps;

export function MobileFilters(props: Props) {
  const { containerStyle, onApply, activeFilters, markets, neighborhoods } =
    props;
  const [filterValues, setFilterValues] = useState<FilterParamsType>(
    _pick(activeFilters, [
      'market',
      'neighborhoods',
      'price',
      'beds',
      'bedrooms',
      'bathrooms',
      'amenities',
      'guests',
    ])
  );
  const [showFiltersContainer, setShowFiltersContainer] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (activeFilters.market) {
      count++;
    }
    if (activeFilters.neighborhoods?.length) {
      count++;
    }
    if (activeFilters.price?.min || activeFilters.price?.max) {
      count++;
    }
    if (activeFilters.beds?.min || activeFilters.beds?.max) {
      count++;
    }
    if (activeFilters.bedrooms?.min || activeFilters.bedrooms?.max) {
      count++;
    }
    if (activeFilters.bathrooms?.min || activeFilters.bathrooms?.max) {
      count++;
    }
    if (activeFilters.amenities?.length) {
      count++;
    }
    if (activeFilters.guests) {
      count++;
    }
    return count;
  }, [activeFilters]);

  const openFiltersContainer = () => {
    setShowFiltersContainer((value) => !value);
  };

  const closeFiltersContainer = () => {
    setShowFiltersContainer(false);
  };

  useClickOutside(containerRef, closeFiltersContainer);

  const onApplyClick = () => {
    onApply(filterValues);
    closeFiltersContainer();
  };

  const onFilterChange = (filter: string) => (value: any) => {
    setFilterValues((prevFilterValues) => ({
      ...prevFilterValues,
      [filter]: value,
    }));
  };

  return (
    <div
      ref={containerRef}
      className={clsx([styles.mobileFilters, containerStyle])}
    >
      <div
        className={styles.mobileFilters__toggle}
        onClick={openFiltersContainer}
      >
        {activeFiltersCount > 0 ? (
          <span>Filters ({activeFiltersCount})</span>
        ) : (
          <span>Open Filters</span>
        )}
      </div>

      {showFiltersContainer && (
        <div
          className={styles.filtersContainer}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton
            onClick={closeFiltersContainer}
            containerStyle={styles.filtersContainer__closeButton}
          />
          <div className={styles.filtersContainer__wrapper}>
            <div>
              <div className={styles.filtersContainer__title}>Location</div>
              <div className={styles.filtersContainer__locationSelectors}>
                <LocationSelectors
                  markets={markets}
                  neighborhoods={neighborhoods}
                  filters={filterValues}
                  changeMarket={onFilterChange('market')}
                  changeNeighborhoods={onFilterChange('neighborhoods')}
                />
              </div>
            </div>

            <div className={styles.filtersContainer__divider} />

            <div>
              <div className={styles.filtersContainer__title}>
                {renderPriceValue(
                  filterValues.price,
                  filterValues.market === 'new-york-city'
                )}
              </div>
              <MinMaxInputs
                value={filterValues.price}
                onChange={onFilterChange('price')}
                inputStyle={styles.filtersContainer__price}
                type="price"
              />
            </div>

            <div className={styles.filtersContainer__divider} />

            <div>
              <div className={styles.filtersContainer__title}>Beds</div>
              <MinMaxInputs
                value={filterValues.beds}
                onChange={onFilterChange('beds')}
                inputStyle={styles.filtersContainer__input}
                type="rooms"
                placeholder="Any"
              />
            </div>

            <div className={styles.filtersContainer__divider} />

            <div>
              <div className={styles.filtersContainer__title}>
                {renderBedroomsValue(filterValues.bedrooms)}
              </div>
              <MinMaxSelectors
                value={filterValues.bedrooms}
                onChange={onFilterChange('bedrooms')}
                options={bedroomsOptions}
                selectorStyle={styles.filtersContainer__bedrooms}
                emptyValue={'Any'}
              />
            </div>

            <div className={styles.filtersContainer__divider} />

            <div>
              <div className={styles.filtersContainer__title}>Bathrooms</div>
              <MinMaxInputs
                value={filterValues.bathrooms}
                onChange={onFilterChange('bathrooms')}
                inputStyle={styles.filtersContainer__input}
                type="rooms"
                placeholder="Any"
              />
            </div>

            <div className={styles.filtersContainer__divider} />

            <div>
              <div className={styles.filtersContainer__title}>Party Size</div>
              <Select
                value={filterValues.guests}
                options={guestsOptions}
                onSelect={onFilterChange('guests')}
                placeholder="Guests"
                containerStyle={styles.filtersContainer__guests}
                customEmptyValue={{ label: 'Any', value: '' }}
                withRoundCorners
              />
            </div>

            <div className={styles.filtersContainer__divider} />

            <AmenitiesSelection
              value={filterValues.amenities}
              onChange={onFilterChange('amenities')}
            />
          </div>

          <Button
            onClick={onApplyClick}
            containerStyle={styles.confirm}
            variant="darkTanContained"
          >
            Confirm
          </Button>
        </div>
      )}
    </div>
  );
}
